import { useCallback, useRef, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { hasAsyncPlaceholders, interpolateTextAsync } from "../../shared/template";
import type { TemplateContext } from "../../shared/template";
import { useEnduserStore } from "../store/hooks";
import { useOrganization } from "../../shared/store/organizationStore";
import { useSeasonBanner } from "../../shared/store/portalSchemaStore";
import { peekPendingPartnerRedirect } from "../utils/pendingPartnerRedirect";
import { appendRedirectQueryParam } from "../utils/appendRedirectQueryParam";

interface AsyncLinkInterceptorProps {
  children: ReactNode;
}

const findAnchorAncestor = (start: EventTarget | null, container: HTMLElement | null): HTMLAnchorElement | null => {
  if (!(start instanceof Node) || !container) return null;
  let node: Node | null = start;
  while (node && node !== container) {
    if (node instanceof HTMLAnchorElement) return node;
    node = node.parentNode;
  }
  return null;
};

const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Wraps user-authored rich-text/HTML content and intercepts clicks on anchors
 * whose href contains async template placeholders (e.g. {{season.token}}).
 * Resolves the URL via interpolateTextAsync at click time and opens it.
 *
 * Clicks on plain links pass through to the browser's native handling
 * (preserving middle-click, Cmd-click, copy-link-address).
 */
export const AsyncLinkInterceptor = ({ children }: AsyncLinkInterceptorProps) => {
  const { t } = useTranslation("common");
  const session = useEnduserStore((state) => state.session);
  const enduser = useEnduserStore((state) => state.currentEnduser);
  const organization = useOrganization();
  const seasonBanner = useSeasonBanner();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLDivElement>) => {
      if (!isPlainLeftClick(event)) return;

      const anchor = findAnchorAncestor(event.target, containerRef.current);
      if (!anchor) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref || !hasAsyncPlaceholders(rawHref)) return;

      event.preventDefault();
      event.stopPropagation();

      if (!session) {
        toast.error(t("Unable to open link. Please try again."));
        return;
      }

      const templateContext: TemplateContext = {
        enduser,
        organization,
        snippets: {},
      };

      try {
        const resolved = await interpolateTextAsync(
          rawHref,
          templateContext,
          { session, enduser, organization },
          { banner: seasonBanner ?? "" }
        );
        const finalUrl = appendRedirectQueryParam(resolved, peekPendingPartnerRedirect());
        window.open(finalUrl, "_blank", "noopener,noreferrer");
      } catch {
        toast.error(t("Unable to open link. Please try again."));
      }
    },
    [session, enduser, organization, seasonBanner, t]
  );

  return (
    <div ref={containerRef} onClick={handleClick}>
      {children}
    </div>
  );
};
